import React from 'react';
import { motion } from 'framer-motion';

const experiences = [
  {
    type: "work",
    title: "Software Engineer",
    organization: "James McGuinness & Associates",
    duration: "May 2026 – Present",
    location: "Schenectady, NY",
    description: [
      "Built C#/.NET full-stack features for enterprise applications, integrating UI workflows with SQL Server-backed data models.",
      "Designed relational schemas and authored stored procedures in SSMS for reporting and data integrity.",
      "Developed dynamic form prefill functionality that auto-populates fields from related entities, reducing data entry errors.",
      "Tech: C#, .NET, Microsoft SQL Server, SSMS, SQL, Visual Studio."
    ]
  },
  {
    type: "work",
    title: "Tech Co-op (Software Engineering), Carbon Management",
    organization: "Wood Mackenzie",
    duration: "July 2025 – Present",
    location: "Boston, MA",
    description: [
      "Built time-series dashboards with custom charts, cross-filters, and drilldowns on multimillion-row data to speed analysis.",
      "Optimized REST and GraphQL APIs with input validation and pagination to improve reliability and throughput.",
      "Automated Jenkins CI/CD for services and ETL; cut deployment errors by 40% and ETL runtime by 25%, speeding releases.",
      "Collaborated with data scientists, product managers, and UX engineers to ship 5+ features in Scrum and triage P1s within 24 hours.",
      "Refactored legacy modules and expanded tests (TDD), cutting bug recurrence by 20% and improving maintainability."
    ]
  },
  {
    type: "work",
    title: "Web Developer Co-op",
    organization: "Boston Globe Media Partners",
    duration: "July 2024 - December 2024",
    location: "Boston, MA",
    description: [
      "Built and deployed 10+ interactive features using Agile and CI/CD, enhancing user engagement and site functionality",
      "Optimized AMP pages, improving mobile performance for 25% of site traffic",
      "Resolved 100+ issues through TDD and debugging tools, improving UI/UX consistency and performance",
      "Collaborated with designers and PMs in a Scrum environment to refine UX and increase feature adoption by 20%"
    ]
  },
  {
    type: "work",
    title: "Founder and Developer",
    organization: "Code4Good",
    duration: "July 2020 - June 2024",
    location: "Karnal, India",
    description: [
      "Launched 10+ civic tech web apps, impacting 16.5M+ users across 11 districts",
      "Automated RT-PCR testing for 3M+ patients via a government-used report generation portal",
      "Developed real-time dashboards for hospital bed tracking during COVID-19 emergencies",
      "Awarded India’s Prime Minister’s National Child Award for youth innovation and public impact"
    ]
  },
  {
    type: "work",
    title: "Teaching Assistant for CS 2500",
    organization: "Northeastern University",
    duration: "September 2023 - December 2023",
    location: "Boston, MA",
    description: [
      "Assisted in curriculum development and hands-on learning for CS 2500 (Fundamentals of Computer Science)",
      "Led code review sessions and office hours, enhancing student comprehension and debugging proficiency",
      "Graded assignments and supported learning outcomes for a class of 30+ students"
    ]
  },
  {
    type: "work",
    title: "Front End Web Developer Intern",
    organization: "Fitterfly",
    duration: "October 2021 - February 2022",
    location: "Mumbai, India",
    description: [
      "Enhanced 10+ web pages using HTML, CSS, and AJAX within a fast-paced startup environment",
      "Built a real-time cloud dashboard for fitness tracking used in clinical consultations",
      "Contributed to collaborative development processes while adapting to startup-style delivery cycles"
    ]
  },
  {
    type: "education",
    title: "Bachelor of Science in Computer Science",
    organization: "Northeastern University",
    duration: "September 2022 - May 2026 (Expected)",
    location: "Boston, MA",
    description: [
      "GPA: 3.89/4.00 | Dean’s List every semester",
      "Completed coursework in Software Engineering, AI, Systems, Cybersecurity, and Algorithms",
      "Relevant skills in Python, JavaScript, C++, React, Node.js, AWS, and Docker"
    ]
  }
];


const ExperienceCard = ({ experience, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.2 }}
      className={`relative flex items-center mb-12 ${
        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
      }`}
    >
      {/* Timeline dot with ping animation */}
      <div className="absolute left-4 md:left-1/2 -translate-x-1/2 z-10">
        <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 relative">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 animate-ping opacity-50" />
        </div>
      </div>

      {/* Content */}
      <div className={`ml-12 md:ml-0 md:w-1/2 ${
        index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'
      }`}>
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-xl border border-white/[0.1] p-6 backdrop-blur-sm
                    transition-colors duration-300 hover:border-white/[0.2] hover:bg-white/[0.05]"
        >
          <div className="relative z-10">
            {/* Type Badge */}
            <span className={`inline-block px-3 py-1 mb-4 text-sm font-medium rounded-full 
              ${experience.type === 'work' 
                ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-200 border border-purple-500/30' 
                : 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-200 border border-blue-500/30'}`}
            >
              {experience.type === 'work' ? 'Work Experience' : 'Education'}
            </span>

            {/* Title & Organization */}
            <h3 className="text-xl font-semibold mb-2 bg-gradient-to-r from-white via-white/90 to-white text-transparent bg-clip-text">
              {experience.title}
            </h3>
            <div className="text-lg text-white/80 mb-2">{experience.organization}</div>

            {/* Duration & Location */}
            <div className="flex justify-between text-sm text-white/60 mb-4">
              <span>{experience.duration}</span>
              <span>{experience.location}</span>
            </div>

            {/* Description */}
            <ul className="space-y-2">
              {experience.description.map((item, idx) => (
                <motion.li 
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 + idx * 0.1 }}
                  className="flex items-start text-white/70"
                >
                  <span className="mr-2 mt-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" />
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Background gradient */}
          <div className={`absolute inset-0 opacity-[0.03] bg-gradient-to-br 
            ${experience.type === 'work' 
              ? 'from-purple-500 to-pink-500' 
              : 'from-blue-500 to-cyan-500'}`} 
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export const Experience = () => {
  return (
    <div className="container mx-auto px-4 pt-8 pb-16">
      {/* Section Header */}
      <div className="max-w-6xl mx-auto text-center pt-8 pb-12">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-white via-white/90 to-white text-transparent bg-clip-text">
          Experience & Education
        </h2>
        <p className="text-gray-400 mt-4">
          My professional journey and academic background
        </p>
      </div>

      {/* Timeline */}
      <div className="max-w-6xl mx-auto">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-1/2 h-full w-0.5 bg-gradient-to-b from-purple-500/50 via-pink-500/50 to-purple-500/50" />

          {/* Experience items */}
          {experiences.map((experience, index) => (
            <ExperienceCard key={index} experience={experience} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Experience; 